import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, SubmitHandler, UseFormReturn, useController, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AppointmentModel } from '@devexpress/dx-react-scheduler'

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material'

import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'

const schema = z.object({
  deleteType: z.string().min(1),
  deleteTo: z.string().optional(),
})

export type DeleteScheduleSelectionFormData = z.infer<typeof schema>

function DeleteToField({ minDate, rhForm, label }: { minDate?: Dayjs; rhForm: UseFormReturn<DeleteScheduleSelectionFormData>; label: string }) {
  const { field } = useController({ control: rhForm.control, name: 'deleteTo' })
  const deleteTypeValue = useWatch({ control: rhForm.control, name: 'deleteType' })

  useEffect(() => {
    if (deleteTypeValue !== '1') rhForm.resetField('deleteTo')
  }, [deleteTypeValue, rhForm])

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <span>{label}</span>
      <DatePickerInput
        disabled={deleteTypeValue !== '1'}
        minDate={minDate}
        onChange={(date) => {
          rhForm.setValue('deleteTo', date?.format('YYYY-MM-DD') ?? '')
        }}
        value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
      />
    </Stack>
  )
}

type DeleteScheduleDialogProps = {
  appointmentData: AppointmentModel | undefined
  onClose?: () => void
  onSubmit: SubmitHandler<DeleteScheduleSelectionFormData>
  open: boolean
}

export function DeleteScheduleDialog(props: DeleteScheduleDialogProps) {
  const { appointmentData, onClose, onSubmit, open } = props

  const { t } = useTranslation()

  const rhForm = useForm<DeleteScheduleSelectionFormData>({ defaultValues: { deleteType: '0' }, resolver: zodResolver(schema) })

  const options = useMemo(
    () => [
      { label: t('page.agenda.deleteSchedule.thisSchedule', 'This schedule'), value: '0' },
      {
        label: <DeleteToField label={t('page.agenda.deleteSchedule.thisAndFollowingTill', 'This and following schedules till')} minDate={appointmentData?.startDate ? dayjs(appointmentData.startdate) : undefined} rhForm={rhForm} />,
        value: '1',
      },
      { label: t('page.agenda.deleteSchedule.thisAndAllFollowing', 'This and all following schedules'), value: '2' },
      { label: t('page.agenda.deleteSchedule.allSchedules', 'All schedules'), value: '3' },
    ],
    [appointmentData, rhForm, t]
  )

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('page.agenda.deleteSchedule.title', 'Delete schedule')}</DialogTitle>
      <DialogContent>
        <Controller
          control={rhForm.control}
          name="deleteType"
          render={({ field }) => (
            <RadioGroupInput
              onChange={(_event, value) => {
                rhForm.setValue('deleteType', value)
              }}
              options={options}
              value={field.value}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            rhForm.reset()
            onClose?.()
          }}
        >
          {t('common:button.cancel.label')}
        </Button>
        <Button color="error" onClick={rhForm.handleSubmit(onSubmit)} variant="outlined">
          {t('common:button.delete.label')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
